import type {
  AppStateBackupOperation,
  AppStateBackupOperationToken,
  AppStateBackupProgressEvent,
} from './appStateBackupProgress'

export type AppStateBackupProgressSignal =
  | { kind: 'begin'; token: AppStateBackupOperationToken; operation: AppStateBackupOperation }
  | { kind: 'report'; token: AppStateBackupOperationToken; event: AppStateBackupProgressEvent }
  | {
      kind: 'complete'
      token: AppStateBackupOperationToken
      operation: AppStateBackupOperation
      message: string
      persistAcrossReload: boolean
    }
  | { kind: 'fail'; token: AppStateBackupOperationToken; operation: AppStateBackupOperation; message: string }

type ProgressSignalListener = (signal: AppStateBackupProgressSignal) => void

let activeOperation: { token: AppStateBackupOperationToken; operation: AppStateBackupOperation } | null = null
let operationSequence = 0
let latestSignal: AppStateBackupProgressSignal | null = null
const listeners = new Set<ProgressSignalListener>()

const deliver = (listener: ProgressSignalListener, signal: AppStateBackupProgressSignal): void => {
  try {
    listener(signal)
  } catch (error) {
    console.warn('Backup progress display listener failed; the backup operation will continue safely.', error)
  }
}

const emit = (signal: AppStateBackupProgressSignal): void => {
  latestSignal = signal
  listeners.forEach(listener => deliver(listener, signal))
}

export const subscribeToAppStateBackupProgress = (listener: ProgressSignalListener): (() => void) => {
  listeners.add(listener)
  if (latestSignal) deliver(listener, latestSignal)
  return () => listeners.delete(listener)
}

export const getActiveAppStateBackupOperation = (): AppStateBackupOperation | null => activeOperation?.operation || null

export const isActiveAppStateBackupOperation = (
  token: AppStateBackupOperationToken,
  operation: AppStateBackupOperation
): boolean => activeOperation?.token === token && activeOperation.operation === operation

export const clearAppStateBackupProgressDisplay = (): void => {
  if (!activeOperation) latestSignal = null
}

export const beginAppStateBackupProgress = (
  operation: AppStateBackupOperation
): AppStateBackupOperationToken | null => {
  if (activeOperation) return null
  operationSequence += 1
  const token = `${operation}-${Date.now()}-${operationSequence}`
  activeOperation = { token, operation }
  emit({ kind: 'begin', token, operation })
  return token
}

export const reportAppStateBackupProgress = (
  token: AppStateBackupOperationToken,
  event: AppStateBackupProgressEvent
): void => {
  if (!activeOperation || activeOperation.token !== token || activeOperation.operation !== event.operation) return
  emit({ kind: 'report', token, event })
}

export const completeAppStateBackupProgress = (
  token: AppStateBackupOperationToken,
  operation: AppStateBackupOperation,
  message: string,
  persistAcrossReload = false
): void => {
  if (!activeOperation || activeOperation.token !== token || activeOperation.operation !== operation) return
  activeOperation = null
  emit({ kind: 'complete', token, operation, message, persistAcrossReload })
}

export const failAppStateBackupProgress = (
  token: AppStateBackupOperationToken,
  operation: AppStateBackupOperation,
  message: string
): void => {
  if (!activeOperation || activeOperation.token !== token || activeOperation.operation !== operation) return
  activeOperation = null
  emit({ kind: 'fail', token, operation, message })
}

/** Test isolation for the module-level owner. Application code must finish via complete/fail. */
export const resetAppStateBackupProgressForTests = (): void => {
  activeOperation = null
  latestSignal = null
}
